import * as React from 'react'

import { action, makeObservable, observable, runInAction } from 'mobx'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import Linkifier from '~/client/src/shared/components/Linkifier/Linkifier'
import MenuCloser from '~/client/src/shared/components/MenuCloser'
import {
  autoResizeTextarea,
  observeTextareaResize,
  supportsFieldSizingContent,
} from '~/client/src/shared/utils/autoResizeTextarea'
import { EMPTY_STRING } from '~/client/src/shared/utils/usefulStrings'

import BaseStruxhubInput, { ISharedProps } from './BaseStruxhubInput'

// localization: no text to translate

interface IProps extends ISharedProps {
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void

  id?: string
  placeholder?: string
  defaultValue?: string
  isLinkified?: boolean
  maxLength?: number
  autoFocus?: boolean
  onFocus?: () => void
  onBlur?: () => void
  onKeyPress?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void
  rows?: number
}

@observer
export default class StruxhubTextArea extends React.Component<IProps> {
  @observable private isEditMode: boolean = false
  @observable private value: string
  @observable private localProps: IProps = null
  private textAreaElement: HTMLTextAreaElement | null = null
  private disposeResizeObserver: (() => void) | null = null

  public constructor(props: IProps) {
    super(props)

    this.localProps = props

    this.setValue()

    makeObservable(this)
  }

  public componentDidMount() {
    autoResizeTextarea(this.textAreaElement, this.props.rows)
    if (this.textAreaElement && !this.props.rows) {
      this.disposeResizeObserver = observeTextareaResize(
        this.textAreaElement,
        this.props.rows,
      )
    }
  }

  public componentWillUnmount() {
    this.disposeResizeObserver?.()
  }

  public componentDidUpdate(prevProps: IProps) {
    if (prevProps !== this.props) {
      runInAction(() => {
        this.localProps = this.props
      })
    }

    this.setValue()
    if (prevProps.value !== this.props.value) {
      autoResizeTextarea(this.textAreaElement, this.props.rows)
    }
  }

  public render() {
    return (
      <BaseStruxhubInput {...this.localProps}>
        {(isValueInvalid, isInFocus, onFocus, onBlur) => {
          if (!this.localProps.isLinkified) {
            return this.renderTextArea(
              isValueInvalid,
              isInFocus,
              onFocus,
              onBlur,
            )
          }

          return (
            <MenuCloser
              closeMenu={this.disableEditMode}
              isOpen={this.isEditMode}
            >
              <div onClick={this.enableEditMode}>
                {this.renderValue(isValueInvalid, isInFocus, onFocus, onBlur)}
              </div>
            </MenuCloser>
          )
        }}
      </BaseStruxhubInput>
    )
  }

  private renderValue(
    isValueInvalid: boolean,
    isInFocus: boolean,
    onFocus: () => void,
    onBlur: () => void,
  ): JSX.Element {
    const { value } = this.localProps

    if (this.isEditMode) {
      return this.renderTextArea(isValueInvalid, isInFocus, onFocus, onBlur)
    }

    return (
      <div
        className={classList({
          'pre-wrap bg-palette-brand-lightest no-outline brada4 px8 py7 min-height66 full-width overflow-hidden text line-24 extra-large tw:break-words': true,
          'ba-palette-brand-lighter': !isValueInvalid,
          'ba-red': isValueInvalid,
        })}
      >
        <Linkifier linkClassName="no-flex" value={value} />
      </div>
    )
  }

  private renderTextArea(
    isValueInvalid: boolean,
    isInFocus: boolean,
    onFocus: () => void,
    onBlur: () => void,
  ): JSX.Element {
    const {
      id,
      defaultValue,
      placeholder,
      isRequired,
      isDisabled,
      isReadOnly,
      isLinkified,
      maxLength,
      autoFocus,
      onKeyPress,
      rows,
    } = this.localProps

    return (
      <textarea
        id={id}
        ref={this.setRef}
        className={classList({
          'row bg-palette-brand-lightest no-outline brada4 px8 py7 min-height66 no-resize full-width text line-24 extra-large': true,
          'tw:field-sizing-content': !rows && supportsFieldSizingContent,
          'overflow-hidden': !rows,
          'overflow-y-scroll': !!rows,
          'ba-light-blue': isInFocus && !isValueInvalid,
          'ba-palette-brand-lighter': !isInFocus && !isValueInvalid,
          'ba-red': isValueInvalid,
        })}
        autoFocus={isLinkified || autoFocus}
        onFocus={this.onFocus.bind(this, onFocus)}
        onBlur={this.onBlur.bind(this, onBlur)}
        onKeyPress={onKeyPress}
        placeholder={placeholder}
        value={this.value}
        defaultValue={defaultValue}
        maxLength={maxLength}
        required={isRequired}
        onChange={this.onChange}
        disabled={isDisabled}
        readOnly={isReadOnly}
        rows={rows}
      />
    )
  }

  @action.bound
  private enableEditMode() {
    const { isDisabled, isReadOnly } = this.props

    if (!isDisabled && !isReadOnly) {
      this.isEditMode = true
    }
  }

  @action.bound
  private disableEditMode() {
    this.isEditMode = false
  }

  private onFocus(
    onFocus: () => void,
    e: React.FocusEvent<HTMLTextAreaElement>,
  ) {
    const currentValue = e.target.value
    e.target.value = EMPTY_STRING
    e.target.value = currentValue

    onFocus?.()
    Promise.resolve().then(() =>
      autoResizeTextarea(this.textAreaElement, this.props.rows),
    )
    this.props.onFocus?.()
  }

  private onBlur(onBlur: () => void) {
    onBlur?.()
    this.props.onBlur?.()
  }

  private setRef = (ref: HTMLTextAreaElement) => {
    this.textAreaElement = ref
  }

  @action.bound
  private setValue(): void {
    if (this.localProps.value !== this.value) {
      this.value = this.localProps.value
    }
  }

  @action.bound
  private onChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
    this.value = event?.target?.value
    autoResizeTextarea(this.textAreaElement, this.props.rows)
    this.localProps.onChange?.(event)
  }
}
